import { Box, Tooltip } from "@mui/material";
import { type MRT_ColumnDef } from "material-react-table";
import { Link } from 'react-router-dom';
import {
  capitalizeFirstLetter,
  validateDate,
  formatCurrency,
  formatDurationFromSeconds,
} from "../../utils/utils";
import "../../styles/common.css"

const initialSort = { sort_field: "domain", sort_order: "asc" };
const displayValue = (val: any) => {
  if (val === null || val === undefined || val === "") return "---";
  return val;
};

const getColumns = (): MRT_ColumnDef<any>[] => [
  {
    accessorKey: "analystTime",
    header: "Analyst Name",
    Cell: ({ cell }: { cell: any }) => {
      return (
        <span style={{ fontWeight: 500 }}>
          {cell.getValue() || "-"}
        </span>
      );
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 150,
    size: 180,
  },
  {
    accessorKey: "managerTime",
    header:  "Manager Name",
    Cell: ({ cell }: { cell: any }) => {
      return (
        <span style={{ fontWeight: 500 }}>
          {cell.getValue() || "-"}
        </span>
      );
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 150,
    size: 180,
  },
  {
    accessorKey: "domain",
    header: "Client Name",
    Cell: ({ row }: { row: any }) => {
      const clientName = row.original?.clientName;
      const clientDomain = row.original?.domain;
      const formattedName = capitalizeFirstLetter(clientName);
      const isSelected = row.original?.highlightedClient === clientDomain;
      const userEmail = row.original?.userEmail;
      const UserProfileOrgId = row.original?.userProfileOrgId;
      const clientOrgId = row.original?.clientOrgId;
      const subOrgId = row.original?.subOrgId;

      const handleClientClick = () => {
        sessionStorage.setItem("selectedClient", clientDomain);
        row.original?.onClientClick(clientDomain, clientName, true, clientOrgId, subOrgId, userEmail, UserProfileOrgId);
      };
      return <Box className="flex flex-row gap-1 justify-center"
      sx={{
        justifyContent: "flex-start",
        alignItems: "flex-start",
        alignContent: "flex-start",
        backgroundColor: isSelected ? "#bd00cd2e" : "transparent",
        borderRadius: isSelected ? "4px" : "0px",
        padding: "4px 8px",
      }}
    >
        <span
        style={{
            flexGrow: 0,
            flexShrink: 1,
            flexBasis: "auto",
            textDecoration: "underline",
            textDecorationColor: "rgba(25, 118, 210, 0.4)",
            color: "#1976d2",
            cursor: "pointer",
          }}
          onClick={handleClientClick}
        >
      {displayValue(formattedName)}
        </span>
    </Box>
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 250,
    size: 250,
  },
  {
    accessorKey: "mrr",
    header: "MRR",
    Cell: ({ cell }: { cell: any }) => {
      return <Box>{displayValue(formatCurrency(cell.getValue() || 0))}</Box>;
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 110,
    size: 110,
  },
  {
    id:"interactions",
    accessorKey: "num_meetings",
    header: "No. of Interactions",
    Cell: ({ cell, row }: { cell: any; row: any }) => {
      const userEmail = row.original?.userEmail;
      const UserProfileOrgId = row.original?.userProfileOrgId;
      const clientOrgId = row.original?.clientOrgId;
      const subOrgId = row.original?.subOrgId;
      const clientDomain = row.original?.domain;
      const clientName = row.original?.clientName;
      const handleClientClick = () => {
        row.original?.onClientClick(clientDomain, clientName, true, clientOrgId, subOrgId, userEmail, UserProfileOrgId);
      };
      return <Box className="flex flex-row gap-1"
        sx={{
          justifyContent: "flex-start",
          alignItems: "flex-start",
          alignContent: "flex-start",
        }}
      >
        <span
          style={{
            flexGrow: 0,
            flexShrink: 1,
            flexBasis: "auto",
            textDecoration: "underline",
            textDecorationColor: "rgba(25, 118, 210, 0.4)",
            color: "#1976d2",
            cursor: "pointer",
          }}
          onClick={handleClientClick}
        >
          {`${displayValue(cell.getValue() || 0)}`}
        </span>
      </Box>
    },
    enableColumnFilter: false,
    enableSorting: true,
  },
  {
    accessorKey: "lastInteraction",
    header: "Last Interaction Date",
    Cell: ({ cell, row }: { cell: any; row: any }) => {
      const summary = row.original?.last_interaction_summary;
      return <Box>
        <Tooltip title={summary} placement="top">
            <span>
            {`${displayValue(validateDate(cell.getValue(), "MMM DD, YYYY"))}`}
            </span>
        </Tooltip>
      </Box>;
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 240,
    maxSize: 200,
    size: 240,
  },
  {
    accessorKey: "managerTalkTime",
    header: "Manager Time",
    Cell: ({ cell }: { cell: any }) => {
      return (
        <span>
          {formatDurationFromSeconds(cell.getValue()) || "-"}
        </span>
      );
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 120,
    size: 120,
  },
  {
    accessorKey: "analystTalkTime",
    header: "Analyst Time",
    Cell: ({ cell }: { cell: any }) => {
      return (
        <span>
          {formatDurationFromSeconds(cell.getValue()) || "-"}
        </span>
      );
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 120,
    size: 120,
  },
  {
    accessorKey: "last_sentiment",
    header: "Last 5 Rating",
    Cell: ({ cell, row }: { cell: any; row: any }) => {
      const sentiments = cell.getValue() || [];

      const hasValidSentiment = sentiments.some(
        (s: any) => s.overall_sentiment
      );
    
      if (!hasValidSentiment) {
        return <Box>---</Box>; // Show "---" if no valid sentiment
      }

      return (
        <Box display="flex" flexDirection="row">
        {sentiments.length > 0
          ? (sentiments
              .filter((sentiment: any) => {
                // Ensure circle is only displayed if either overall_sentiment or classification_score exists
                return sentiment.overall_sentiment || sentiment.classification_score;
              })
              .sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()) // Sort by start_time in ascending order
              .map((sentiment: {
                start_time: any; interaction_id: string; summary: string; overall_sentiment: string, classification_score: string
              }, index: number) => {
                let color = ''; // Default color for neutral
                const sentimentValue = sentiment.overall_sentiment || sentiment.classification_score;

                if (sentimentValue && sentimentValue.toLowerCase() === 'positive') {
                  color = '#04BD00';
                } else if (sentimentValue && sentimentValue.toLowerCase() === 'negative') {
                  color = '#F4442F';
                } else if (sentimentValue && sentimentValue.toLowerCase() === 'neutral') {
                  color = '#FFCD2A';
                }

              return (
                <Tooltip
                  key={index}
                  title={
                    <>
                      <div>{sentiment.summary}</div>
                      <div>{validateDate(sentiment.start_time, "DD MMM YYYY")}</div> {/* Add start_time */}

                    </>
                  }
                  placement="top"
                  arrow
                >
                  <Box
                    display="flex"
                    flexDirection= "row"
                    alignItems= "flex-start"
                    marginRight={0.5}
                    sx={{ cursor: 'pointer', marginLeft: "3px" }}
                  >
                    <Link to={`/meeting/${sentiment.interaction_id}?${(() => {
                        const params = new URLSearchParams();
                        params.set("ci", "1");
                        params.set("ti", "1");
                        return params.toString();
                      })()}`}
                    state={{ selectedNps: row.original?.selectedNps,
                      is_team_meeting_id: row.original.isTeamMeeting,
                      parent_page_identifier: row.original?.parentPageIdentifier,
                      startEndMeetingTime: row.original?.startEndMeetingTime,
                      tableFilters: row.original?.tableFilters,
                      isClientInteraction: row.original?.isClientInteraction,
                      slideOverParent: row.original?.slideOverParent,
                      customDateRange: row.original?.customDateRange,
                      clickeduserEmail: row.original?.clickeduserEmail,
                      clientName: row.original?.clientName,
                      activeStat: row.original?.activeStat,
                    }}
                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                      <Box
                        width={14}
                        height={14}
                        borderRadius="3px"
                        bgcolor={color}
                        marginRight={0.5}
                      />
                    </Link>
                  </Box>
                </Tooltip>
              );
            })
          ): null }
        </Box>
      );
    },
    enableColumnFilter: false,
    enableSorting: false,
    grow: true,
    minSize: 125,
    maxSize: 120,
    size: 125,
  },
];

//Days since Last meeting
export { getColumns, initialSort };

